import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
  Brush,
} from "recharts";
import { Segmented } from "./ui";
import { Activity } from "@/lib/types";
import { fmtPace, fmtSpeed, fmtPace100, fmtNum, fmtHr, fmtDate, fmtDateWeekday, fmtDateTime, fmtDur, CAT_COLORS } from "@/lib/format";
import { useUI, Palette } from "@/lib/theme";
import {
  day,
  weekStartOf,
  round2,
  isoOf,
  rgba,
  HEAT_OPACITY,
  heatLevel,
  CELL,
  MESES,
  MONTHS,
  normGearId,
  Period,
  Metric,
  aggWeek,
  Granularity,
  bucketOf,
  TrendKind,
  trendValue,
} from "@/lib/dash";

/* ---------- helpers de estilo de gráficos (según tema) ---------- */
export function tipProps(p: Palette) {
  return {
    contentStyle: {
      background: p.tipBg,
      border: `1px solid ${p.tipBorder}`,
      borderRadius: 12,
      color: p.tipText,
    },
    labelStyle: { color: p.tipLabel },
    itemStyle: { color: p.tipText },
  };
}
export const axisProps = (p: Palette) => ({ stroke: p.axis, fontSize: 12 });

/** Tooltip del donut: resumen de la categoría (según el filtro temporal activo). */
export function DonutTooltip({ active, payload, t, loc, palette }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const row = (label: string, value: string) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
      <span style={{ color: palette.tipLabel }}>{label}</span>
      <span style={{ color: palette.tipText, fontWeight: 600 }}>{value}</span>
    </div>
  );
  return (
    <div
      style={{
        background: palette.tipBg,
        border: `1px solid ${palette.tipBorder}`,
        borderRadius: 12,
        padding: "8px 12px",
        fontSize: 12,
        minWidth: 150,
      }}
    >
      <div style={{ color: CAT_COLORS[d.cat] ?? palette.tipText, fontWeight: 700, marginBottom: 4 }}>
        {d.name}
      </div>
      {row(t("kpi_actividades"), fmtNum(d.value, 0, loc))}
      {row(t("kpi_kilometros"), fmtNum(d.km, 0, loc))}
      {row(t("kpi_horas"), fmtNum(d.hours, 1, loc))}
      {d.kcal > 0 && row(t("kpi_calorias"), `${fmtNum(d.kcal, 0, loc)} kcal`)}
    </div>
  );
}

/** Tooltip de una zona de FC: nombre Strava, tiempo total y % del tiempo. */
export function ZoneTooltip({ active, payload, t, palette }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const color = payload[0].color ?? palette.tipText;
  const row = (label: string, value: string) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
      <span style={{ color: palette.tipLabel }}>{label}</span>
      <span style={{ color: palette.tipText, fontWeight: 600 }}>{value}</span>
    </div>
  );
  return (
    <div
      style={{
        background: palette.tipBg,
        border: `1px solid ${palette.tipBorder}`,
        borderRadius: 12,
        padding: "8px 12px",
        fontSize: 12,
        minWidth: 160,
      }}
    >
      <div style={{ color, fontWeight: 700, marginBottom: 4 }}>
        {d.name} · {d.label}
      </div>
      {row(t("z_tiempo"), fmtDur(d.mins))}
      {row(t("z_pct"), `${Math.round(d.pct * 100)}%`)}
    </div>
  );
}

/* ===================== TIMELINE INTERACTIVO (reutilizable, día o semana) ===================== */
/** Etiqueta del eje X. En semanal: el mes en la 1ª semana de cada `monthStep`.
 *  En diario: 'd mes' con una densidad de etiquetas acorde al nº de barras. */
function BucketTick({ x, y, payload, lang, monthStep = 1, granularity = "week", buckets = [] }: any) {
  const d = day(payload.value);
  const months = lang === "en" ? MONTHS : MESES;
  if (granularity === "day") {
    const n = buckets.length;
    const idx = buckets.indexOf(payload.value);
    const step = n <= 10 ? 1 : n <= 20 ? 2 : n <= 45 ? 5 : 7;
    if (idx < 0 || idx % step !== 0) return null;
    return (
      <text x={x} y={y + 12} textAnchor="middle" fill="currentColor" className="fill-muted" fontSize={11}>
        {`${d.getDate()} ${months[d.getMonth()]}`}
      </text>
    );
  }
  if (d.getDate() > 7) return null; // solo la 1ª semana del mes
  if (d.getMonth() % monthStep !== 0) return null; // 1 etiqueta cada `monthStep` meses
  return (
    <text x={x} y={y + 12} textAnchor="middle" fill="currentColor" className="fill-muted" fontSize={11}>
      {months[d.getMonth()]}
    </text>
  );
}

export function InteractiveWeeks({
  rows,
  weeks,
  bars,
  stacked = true,
  onPeriod,
  resetKey,
  tipFormatter,
  yDecimals,
  accent = "#FC4C02",
  monthStep = 1,
  granularity = "week",
}: {
  rows: Record<string, any>[];
  weeks: string[]; // claves de bucket ('YYYY-MM-DD'): lunes (semanal) o día (diario)
  bars: { key: string; color: string; name: string }[];
  stacked?: boolean;
  onPeriod: (p: Period | null) => void;
  resetKey: unknown;
  tipFormatter?: (v: number | null) => string;
  yDecimals?: boolean;
  accent?: string;
  monthStep?: number;
  granularity?: Granularity;
}) {
  const { palette, loc, lang, t } = useUI();
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [brush, setBrush] = useState<{ start: number; end: number } | null>(null);
  const bucketSpan = granularity === "week" ? 7 : 1; // días que abarca cada barra

  // Reset al cambiar de rango/dataset.
  useEffect(() => {
    setSelectedWeek(null);
    setBrush(null);
  }, [resetKey]);

  const brushActive = brush && !(brush.start === 0 && brush.end === weeks.length - 1);

  // Calcula y notifica el periodo activo.
  useEffect(() => {
    const span = (from: string, toInclusive: string, label: string): Period => {
      const start = day(from);
      const end = day(toInclusive);
      end.setDate(end.getDate() + bucketSpan);
      return { start, end, label };
    };
    let p: Period | null = null;
    if (selectedWeek) p = span(selectedWeek, selectedWeek, `${fmtDate(selectedWeek, loc)}`);
    else if (brushActive && weeks.length)
      p = span(
        weeks[brush!.start],
        weeks[brush!.end],
        `${fmtDate(weeks[brush!.start], loc)} – ${fmtDate(weeks[brush!.end], loc)}`,
      );
    onPeriod(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek, brush, weeks, loc]);

  const yearMarks = useMemo(() => {
    const out: { week: string; year: number }[] = [];
    weeks.forEach((wk, i) => {
      const y = day(wk).getFullYear();
      const prev = i > 0 ? day(weeks[i - 1]).getFullYear() : null;
      if (i === 0 || y !== prev) out.push({ week: wk, year: y });
    });
    return out;
  }, [weeks]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={rows}
        barCategoryGap="18%"
        onClick={(s: any) => {
          const wk = s?.activeLabel as string | undefined;
          if (wk) setSelectedWeek((cur) => (cur === wk ? null : wk));
        }}
      >
        <CartesianGrid stroke={palette.grid} strokeOpacity={0.5} vertical={false} />
        <XAxis
          dataKey="week"
          tick={<BucketTick lang={lang} monthStep={monthStep} granularity={granularity} buckets={weeks} />}
          interval={0}
          {...axisProps(palette)}
        />
        <YAxis {...axisProps(palette)} allowDecimals={yDecimals ?? (!stacked ? false : true)} />
        <Tooltip
          {...tipProps(palette)}
          cursor={{ fill: palette.cursor, fillOpacity: 0.08 }}
          labelFormatter={(v) => fmtDate(String(v), loc)}
          formatter={tipFormatter ? (v: number) => tipFormatter(v) : undefined}
        />
        {bars.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {yearMarks.map((m, idx) => (
          <ReferenceLine
            key={m.year}
            x={m.week}
            stroke={palette.axis}
            strokeOpacity={0.45}
            strokeDasharray="2 3"
            // El primer año cae en el borde izquierdo: lo separamos del eje Y para que no se solape.
            label={{
              value: String(m.year),
              position: "insideTopLeft",
              fill: palette.axis,
              fontSize: 11,
              dx: idx === 0 ? 16 : 4,
            }}
          />
        ))}
        {selectedWeek && <ReferenceLine x={selectedWeek} stroke={accent} strokeWidth={2} />}
        {bars.map((b) => (
          <Bar
            key={b.key}
            dataKey={b.key}
            name={b.name}
            stackId={stacked ? "a" : undefined}
            fill={b.color}
            radius={[2, 2, 0, 0]}
          />
        ))}
        <Brush
          dataKey="week"
          height={24}
          stroke={accent}
          fill={palette.tipBg}
          travellerWidth={8}
          tickFormatter={(v) => {
            const d = day(String(v));
            const mon = (lang === "en" ? MONTHS : MESES)[d.getMonth()];
            return granularity === "day"
              ? `${d.getDate()} ${mon}`
              : `${mon} ${String(d.getFullYear()).slice(2)}`;
          }}
          onChange={(e: any) => setBrush({ start: e.startIndex, end: e.endIndex })}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ===== HEATMAP DIARIO (estilo GitHub): hue = deporte dominante, intensidad = duración ===== */
export function WeekHeatmap({
  acts,
  period,
  onDay,
}: {
  acts: Activity[];
  period: Period | null;
  onDay: (iso: string | null) => void;
}) {
  const { loc, lang, t } = useUI();

  const { columns, byDay } = useMemo(() => {
    if (!acts.length)
      return { columns: [] as string[], byDay: {} as Record<string, { mins: number; cat: string }> };
    const agg: Record<string, Record<string, number>> = {};
    let min = acts[0].start_local,
      max = acts[0].start_local;
    for (const a of acts) {
      const d = a.start_local.slice(0, 10);
      if (a.start_local < min) min = a.start_local;
      if (a.start_local > max) max = a.start_local;
      (agg[d] ??= {})[a.categoria] = (agg[d]?.[a.categoria] ?? 0) + a.duration_min;
    }
    const byDay: Record<string, { mins: number; cat: string }> = {};
    for (const [d, cats] of Object.entries(agg)) {
      const entries = Object.entries(cats);
      const mins = entries.reduce((s, [, v]) => s + v, 0);
      const cat = entries.sort((x, y) => y[1] - x[1])[0][0];
      byDay[d] = { mins, cat };
    }
    const columns: string[] = [];
    const cur = day(weekStartOf(min));
    const end = day(weekStartOf(max));
    while (cur <= end) {
      columns.push(isoOf(cur));
      cur.setDate(cur.getDate() + 7);
    }
    return { columns, byDay };
  }, [acts]);

  const selectedDay =
    period && (period.end.getTime() - period.start.getTime()) / 86400000 <= 1 ? isoOf(period.start) : null;

  const WD = lang === "en" ? ["M", "T", "W", "T", "F", "S", "S"] : ["L", "M", "X", "J", "V", "S", "D"];
  const monAbbr = (iso: string) => (lang === "en" ? MONTHS : MESES)[day(iso).getMonth()];

  if (!columns.length) return <p className="py-8 text-sm text-muted">{t("txt_sin_acts")}</p>;

  const cell = { width: CELL, height: CELL };

  return (
    <div className="w-full overflow-x-auto pb-2">
      <div className="inline-flex flex-col gap-2">
        {/* etiquetas de mes */}
        <div className="flex gap-1" style={{ paddingLeft: CELL + 6 }}>
          {columns.map((mon, i) => {
            const m = day(mon).getMonth();
            const prev = i > 0 ? day(columns[i - 1]).getMonth() : -1;
            return (
              <div key={mon} className="whitespace-nowrap text-[11px] text-muted" style={{ width: CELL }}>
                {m !== prev ? monAbbr(mon) : ""}
              </div>
            );
          })}
        </div>
        {/* rejilla: días de la semana (filas) × semanas (columnas) */}
        <div className="flex gap-1">
          <div className="flex flex-col gap-1 pr-1.5" style={{ width: CELL }}>
            {WD.map((wl, r) => (
              <div
                key={r}
                className="text-[11px] text-muted"
                style={{ height: CELL, lineHeight: `${CELL}px` }}
              >
                {wl}
              </div>
            ))}
          </div>
          {columns.map((mon) => (
            <div key={mon} className="flex flex-col gap-1">
              {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
                const cd = day(mon);
                cd.setDate(cd.getDate() + dow);
                const iso = isoOf(cd);
                const e = byDay[iso];
                const lvl = heatLevel(e?.mins ?? 0);
                const bg = lvl === 0 ? rgba("#9a958a", 0.18) : rgba(CAT_COLORS[e!.cat], HEAT_OPACITY[lvl]);
                const isSel = selectedDay === iso;
                return (
                  <div
                    key={dow}
                    title={
                      e
                        ? `${fmtDate(iso, loc)} · ${fmtDur(e.mins)} · ${t("cat_" + e.cat)}`
                        : fmtDate(iso, loc)
                    }
                    onClick={() => onDay(isSel ? null : iso)}
                    className="rounded-[4px]"
                    style={{
                      ...cell,
                      background: bg,
                      cursor: e ? "pointer" : "default",
                      boxShadow: isSel ? `0 0 0 2px ${CAT_COLORS.Carrera}` : undefined,
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
        {/* leyenda de intensidad */}
        <div
          className="mt-1 flex items-center gap-1.5 text-[11px] text-muted"
          style={{ paddingLeft: CELL + 6 }}
        >
          <span>{t("heat_menos")}</span>
          {HEAT_OPACITY.map((o, i) => (
            <span
              key={i}
              className="h-4 w-4 rounded-[2px]"
              style={{ background: i === 0 ? rgba("#9a958a", 0.18) : rgba("#7b766b", o) }}
            />
          ))}
          <span>{t("heat_mas")}</span>
          <span className="ml-2">· {t("heat_hue")}</span>
        </div>
      </div>
    </div>
  );
}

/** Chip de filtro activo, coloreado según el contexto (categoría / tab). */
export function FilterChip({
  label,
  onClear,
  color = "#FC4C02",
}: {
  label: string;
  onClear: () => void;
  color?: string;
}) {
  return (
    <button
      onClick={onClear}
      className="flex items-center gap-2 rounded-lg border px-3 py-1 text-xs font-medium transition hover:brightness-110"
      style={{ borderColor: color + "66", background: color + "1A", color }}
    >
      <span>{label}</span>
      <span className="text-sm leading-none">×</span>
    </button>
  );
}

/* ===== GRÁFICO DE SEGUIMIENTO CON MÉTRICA SELECCIONABLE (carrera / hyrox) ===== */
export function MetricTimeline({
  acts,
  weeks,
  metrics,
  onPeriod,
  resetKey,
  accent,
  granularity = "week",
}: {
  acts: Activity[];
  weeks: string[]; // claves de bucket (día o semana) que forman el eje X
  metrics: Metric[];
  onPeriod: (p: Period | null) => void;
  resetKey: unknown;
  accent?: string;
  granularity?: Granularity;
}) {
  const { loc } = useUI();
  const [metricKey, setMetricKey] = useState(metrics[0].key);
  const metric = metrics.find((m) => m.key === metricKey) ?? metrics[0];

  // Reinicia la métrica solo si cambia el conjunto (no en cada render).
  const metricsSig = metrics.map((m) => m.key).join();
  useEffect(() => setMetricKey(metrics[0].key), [metricsSig]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const byBucket: Record<string, Activity[]> = {};
    for (const w of weeks) byBucket[w] = [];
    for (const a of acts) {
      const w = bucketOf(a.start_local, granularity);
      (byBucket[w] ??= []).push(a);
    }
    return weeks.map((w) => {
      const value = aggWeek(byBucket[w] ?? [], metric);
      return { week: w, value: value == null ? null : round2(value) };
    });
  }, [acts, weeks, metric, granularity]);

  return (
    <div>
      <div className="mb-3 overflow-x-auto">
        <Segmented
          ariaLabel="metric"
          value={metricKey}
          onChange={setMetricKey}
          options={metrics.map((m) => ({ value: m.key, label: m.label }))}
        />
      </div>
      <InteractiveWeeks
        rows={rows}
        weeks={weeks}
        resetKey={resetKey}
        onPeriod={onPeriod}
        stacked={false}
        yDecimals={metric.agg !== "count"}
        accent={accent}
        granularity={granularity}
        bars={[{ key: "value", color: metric.color, name: metric.label }]}
        tipFormatter={(v) => metric.fmt(v, loc)}
      />
    </div>
  );
}

/* ===================== TABLA DE ACTIVIDADES (ordenable, paginada y expandible) ===================== */
type SortKey =
  | "fecha"
  | "actividad"
  | "tipo"
  | "tiempo"
  | "km"
  | "ritmo"
  | "power"
  | "cadence"
  | "swolf"
  | "hr"
  | "maxhr"
  | "calorias";
const SORTERS: Record<SortKey, (a: Activity) => number | string> = {
  fecha: (a) => a.start_local,
  actividad: (a) => a.name.toLowerCase(),
  tipo: (a) => a.categoria,
  tiempo: (a) => a.duration_min,
  km: (a) => a.distance_km,
  ritmo: (a) => a.pace_min_km ?? Infinity,
  power: (a) => a.weighted_power_watts ?? a.avg_watts ?? 0,
  cadence: (a) => a.avg_cadence ?? 0,
  swolf: (a) => a.avg_swolf ?? 0,
  hr: (a) => a.avg_heartrate ?? 0,
  maxhr: (a) => a.max_heartrate ?? 0,
  calorias: (a) => a.total_calories ?? 0,
};

/** Columna de la tabla: cómo se ordena (key) y cómo se pinta (cell). */
type Col = {
  key: SortKey;
  label: string;
  right?: boolean;
  grow?: boolean;
  pad?: string; // padding horizontal de la celda (por defecto pr-4)
  cell: (a: Activity) => React.ReactNode;
};

const PAGE_SIZES = [10, 25, 50, 75, 100];

export function ActivityTable({
  acts,
  gearMap,
  showPace = true,
  paceKind,
}: {
  acts: Activity[];
  gearMap: Record<string, string>;
  showPace?: boolean;
  /** Tipo de columna de "ritmo": pace (min/km), speed (km/h) o pace100 (s/100m). Si se omite,
   * se deriva de showPace (pace cuando true). `null` la oculta. */
  paceKind?: TrendKind | null;
}) {
  const { t, loc } = useUI();
  // Columna de velocidad/ritmo según disciplina (label + valor + cómo formatearlo).
  const kind: TrendKind | null = paceKind !== undefined ? paceKind : showPace ? "pace" : null;
  const paceCol =
    kind === "speed"
      ? { label: t("th_velocidad"), fmt: (a: Activity) => fmtSpeed(trendValue(a, "speed"), loc) }
      : kind === "pace100"
        ? { label: t("th_ritmo100"), fmt: (a: Activity) => fmtPace100(trendValue(a, "pace100")) }
        : kind === "pace"
          ? { label: t("th_ritmo"), fmt: (a: Activity) => fmtPace(a.pace_min_km) }
          : null;
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "fecha", dir: "desc" });
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    const f = SORTERS[sort.key];
    return [...acts].sort((a, b) => {
      const va = f(a),
        vb = f(b);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [acts, sort]);

  // Volver a la primera página al cambiar filtro, orden o tamaño.
  useEffect(() => setPage(0), [acts, sort, pageSize]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(current * pageSize, current * pageSize + pageSize);

  const clickSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  if (acts.length === 0) return <p className="py-4 text-sm text-muted">{t("txt_sin_acts")}</p>;

  const hasPower = paceKind === "speed" || acts.some((a) => a.weighted_power_watts || a.avg_watts);
  const hasCadence = acts.some((a) => a.avg_cadence);
  const hasSwolf = acts.some((a) => a.avg_swolf);

  const cols: Col[] = [
    {
      key: "fecha",
      label: t("th_fecha"),
      pad: "pr-8",
      cell: (a) => <span className="whitespace-nowrap text-muted">{fmtDateWeekday(a.start_local, loc)}</span>,
    },
    {
      key: "actividad",
      label: t("th_actividad"),
      grow: true,
      cell: (a) => (
        <div className="truncate" title={a.name}>
          {a.name}
        </div>
      ),
    },
    {
      key: "tipo",
      label: t("th_tipo"),
      cell: (a) => (
        <span
          className="rounded-md px-2 py-0.5 text-xs"
          style={{ background: CAT_COLORS[a.categoria] + "33", color: CAT_COLORS[a.categoria] }}
        >
          {t("cat_" + a.categoria)}
        </span>
      ),
    },
    { key: "tiempo", label: t("th_tiempo"), right: true, cell: (a) => fmtDur(a.duration_min) },
    {
      key: "km",
      label: t("th_km"),
      right: true,
      cell: (a) => (a.distance_km ? a.distance_km.toFixed(1) : "-"),
    },
    ...(paceCol
      ? [
          {
            key: "ritmo",
            label: paceCol.label,
            right: true,
            cell: (a: Activity) => paceCol.fmt(a),
          } as Col,
        ]
      : []),
    ...(hasPower
      ? [
          {
            key: "power",
            label: t("th_potencia"),
            right: true,
            cell: (a: Activity) => {
              const w = a.weighted_power_watts ?? a.avg_watts;
              return w ? Math.round(w) : "-";
            },
          } as Col,
        ]
      : []),
    ...(hasCadence
      ? [
          {
            key: "cadence",
            label: t("th_cadencia"),
            right: true,
            cell: (a: Activity) => (a.avg_cadence ? Math.round(a.avg_cadence) : "-"),
          } as Col,
        ]
      : []),
    ...(hasSwolf
      ? [
          {
            key: "swolf",
            label: t("th_swolf"),
            right: true,
            cell: (a: Activity) => (a.avg_swolf ? Math.round(a.avg_swolf) : "-"),
          } as Col,
        ]
      : []),
    {
      key: "hr",
      label: t("th_hr"),
      right: true,
      cell: (a) => (a.avg_heartrate ? Math.round(a.avg_heartrate) : "-"),
    },
    {
      key: "maxhr",
      label: t("th_max_hr"),
      right: true,
      cell: (a) => (a.max_heartrate ? Math.round(a.max_heartrate) : "-"),
    },
    {
      key: "calorias",
      label: t("th_calorias"),
      right: true,
      cell: (a) => (a.total_calories ? Math.round(a.total_calories) : "-"),
    },
  ];

  const from = current * pageSize + 1;
  const to = Math.min(sorted.length, (current + 1) * pageSize);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted">
            <tr className="border-b border-line">
              <th className="w-8 py-2" />
              {cols.map((c) => (
                <th
                  key={c.key}
                  onClick={() => clickSort(c.key)}
                  className={`cursor-pointer select-none whitespace-nowrap py-2 ${c.pad ?? "pr-4"} font-medium hover:text-fg ${c.grow ? "w-full" : ""} ${c.right ? "text-right" : ""}`}
                >
                  {c.label}
                  <span className="ml-1 text-[10px]">
                    {sort.key === c.key ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((a) => (
              <ActivityRow key={a.id} a={a} cols={cols} gearMap={gearMap} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Controles de paginación */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
        <div className="flex items-center gap-2">
          <span>{t("pg_show")}</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="rounded-lg border border-line bg-panel px-2 py-1 text-fg outline-none focus:border-accent"
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span>
            {t("pg_of_total", {
              from: fmtNum(from, 0, loc),
              to: fmtNum(to, 0, loc),
              total: fmtNum(sorted.length, 0, loc),
            })}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(current - 1)}
            disabled={current === 0}
            className="rounded-lg border border-line px-3 py-1 font-medium transition enabled:hover:text-fg disabled:opacity-40"
          >
            ‹ {t("pg_prev")}
          </button>
          <span className="px-2">{t("pg_page", { page: current + 1, total: totalPages })}</span>
          <button
            onClick={() => setPage(current + 1)}
            disabled={current >= totalPages - 1}
            className="rounded-lg border border-line px-3 py-1 font-medium transition enabled:hover:text-fg disabled:opacity-40"
          >
            {t("pg_next")} ›
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted">{label}</div>
      <div className="font-medium text-fg">{value}</div>
    </div>
  );
}

function ActivityRow({ a, cols, gearMap }: { a: Activity; cols: Col[]; gearMap: Record<string, string> }) {
  const { t, loc } = useUI();
  const [open, setOpen] = useState(false);
  const kmh = a.avg_speed ? a.avg_speed * 3.6 : null;
  const gear = a.gear_id ? gearMap[normGearId(a.gear_id)] : null;
  const power = a.weighted_power_watts ?? a.avg_watts;

  return (
    <>
      <tr
        onClick={() => setOpen((o) => !o)}
        className="cursor-pointer border-b border-line/50 transition hover:bg-muted/10"
      >
        <td className="py-2 pr-3 text-center text-muted">
          <span className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}>›</span>
        </td>
        {cols.map((c) => (
          <td
            key={c.key}
            className={`py-2 ${c.pad ?? "pr-4"} ${c.right ? "text-right" : ""} ${c.grow ? "w-full" : "whitespace-nowrap"}`}
          >
            {c.cell(a)}
          </td>
        ))}
      </tr>
      {open && (
        <tr className="border-b border-line/50 bg-panel2/40">
          <td />
          <td colSpan={cols.length} className="px-1 py-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 md:grid-cols-4">
              <Detail label={t("d_fecha_hora")} value={fmtDateTime(a.start_local, loc)} />
              <Detail label={t("d_deporte")} value={a.sport_type} />
              <Detail
                label={t("d_distancia")}
                value={a.distance_km ? `${a.distance_km.toFixed(2)} km` : "-"}
              />
              <Detail label={t("d_duracion")} value={fmtDur(a.duration_min)} />
              <Detail label={t("d_ritmo")} value={fmtPace(a.pace_min_km)} />
              <Detail label={t("d_hr")} value={fmtHr(a.avg_heartrate, loc)} />
              <Detail label={t("d_max_hr")} value={fmtHr(a.max_heartrate, loc)} />
              <Detail label={t("d_vel_media")} value={kmh ? `${kmh.toFixed(1)} km/h` : "-"} />
              {power != null && <Detail label={t("d_potencia")} value={`${Math.round(power)} W`} />}
              {a.avg_cadence != null && <Detail label={t("d_cadencia")} value={`${Math.round(a.avg_cadence)} rpm`} />}
              {a.avg_swolf != null && <Detail label={t("d_swolf")} value={Math.round(a.avg_swolf)} />}
              {a.avg_temp != null && <Detail label={t("d_temp")} value={`${Math.round(a.avg_temp)} °C`} />}
              <Detail label={t("d_desnivel")} value={a.elev ? `${Math.round(a.elev)} m` : "-"} />
              <Detail
                label={t("d_calorias")}
                value={a.total_calories ? `${Math.round(a.total_calories)} kcal` : "-"}
              />
              <Detail label={t("d_carga")} value={Math.round(a.relative_effort)} />
              <Detail label={t("d_kudos")} value={a.kudos_count ?? 0} />
              <Detail label={t("d_pr")} value={a.pr_count ?? 0} />
              <Detail label={t("d_material")} value={gear ?? "-"} />
            </div>
            {a.description && (
              <p className="mt-4 whitespace-pre-wrap border-t border-line/60 pt-3 text-xs leading-relaxed text-muted">
                {a.description}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
